import appScreenshot from '../assets/app-screenshot.png';
import insightsScreenshot from '../assets/insights-screenshot.png';
import './HomePage.css';

const features = [
  {
    icon: '📡',
    title: 'Real-Time CGM Sync',
    desc: 'Connects to LibreView to automatically fetch your FreeStyle Libre data. Your glucose is always up to date.',
  },
  {
    icon: '📈',
    title: 'Interactive Trend Charts',
    desc: 'Visualise your blood glucose over 3, 6, 12, 24, or 48 hours with smooth, interactive charts.',
  },
  {
    icon: '🔔',
    title: 'Smart Alarms',
    desc: 'Configurable low, high, fast-rise, and fast-drop alarms with day and night schedules and custom sounds.',
  },
  {
    icon: '🍽️',
    title: 'Meal Logging',
    desc: 'Log meals by description, photo, cookbook reference, or online recipe. Optional AI-powered nutrition analysis.',
  },
  {
    icon: '💉',
    title: 'Insulin Tracking',
    desc: 'Record bolus and basal doses with a precision dial. Set reminders for your daily basal injection.',
  },
  {
    icon: '🏃',
    title: 'Exercise Logging',
    desc: 'Track workouts and see how activity correlates with your glucose movements on the Insights chart.',
  },
];

const steps = [
  {
    number: '01',
    title: 'Connect your CGM',
    desc: 'Link your LibreView account in Settings. The app immediately starts fetching your FreeStyle Libre data.',
  },
  {
    number: '02',
    title: 'Log your day',
    desc: 'Record meals, insulin doses, and exercise in seconds. Quick-time buttons make it frictionless.',
  },
  {
    number: '03',
    title: 'Understand your patterns',
    desc: 'The Insights screen overlays meals and insulin on your glucose chart so you can see cause and effect at a glance.',
  },
];

export default function HomePage() {
  return (
    <main className="home">

      {/* ── HERO ── */}
      <section className="hero">
        <div className="hero-inner">
          <h1 className="hero-title">
            Understand your blood glucose,<br />
            <span className="hero-highlight">not just monitor it.</span>
          </h1>
          <p className="hero-sub">
            Trenda brings together your CGM data, meals, insulin, and exercise in one
            clear picture — so you can spot patterns and make confident decisions with your care team.
          </p>
          <div className="hero-actions">
            <a
              href="https://apps.apple.com"
              className="btn-primary"
              target="_blank"
              rel="noopener noreferrer"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
              </svg>
              Download on the App Store
            </a>
          </div>
          <p className="hero-fine">iOS only &middot; Free to download</p>
        </div>

        <div className="hero-visual">
          <div className="phone-mockup">
            <img
              src={appScreenshot}
              alt="Trenda app showing blood glucose reading of 4.8 mmol/L with trend chart"
              className="phone-screenshot"
            />
          </div>
        </div>
      </section>

      {/* ── FEATURES ── */}
      <section className="features" id="features">
        <div className="section-inner">
          <div className="section-label">Everything in one place</div>
          <h2 className="section-title">All the tools you need,<br />none of the clutter.</h2>
          <p className="section-sub">
            Trenda is purpose-built for people managing diabetes — designed to reduce
            cognitive load, not add to it.
          </p>
          <div className="features-grid">
            {features.map(f => (
              <div className="feature-card" key={f.title}>
                <span className="feature-icon" role="img" aria-label={f.title}>{f.icon}</span>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="how-it-works" id="how-it-works">
        <div className="section-inner">
          <div className="section-label">Simple to get started</div>
          <h2 className="section-title">Up and running in minutes.</h2>
          <p className="section-sub">
            Connect your existing FreeStyle Libre account — no new hardware or subscriptions needed.
          </p>
          <div className="steps">
            {steps.map((s, i) => (
              <div className="step" key={s.number}>
                <div className="step-number">{s.number}</div>
                <div className="step-content">
                  <h3>{s.title}</h3>
                  <p>{s.desc}</p>
                </div>
                {i < steps.length - 1 && <div className="step-connector" aria-hidden="true" />}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── INSIGHTS HIGHLIGHT ── */}
      <section className="highlight-section">
        <div className="section-inner highlight-inner">
          <div className="highlight-text">
            <div className="section-label">The Insights screen</div>
            <h2 className="section-title">See the full picture.</h2>
            <p>
              The Insights screen overlays your meals and insulin doses directly onto your glucose
              chart. Tap any marker to view details. Use the time slider to zoom in on any period
              of the day.
            </p>
            <ul className="highlight-list">
              <li>Meal markers with nutritional details</li>
              <li>Insulin dose overlay with dose navigator</li>
              <li>3 h – 48 h time windows</li>
              <li>Pull-to-refresh for the latest data</li>
            </ul>
          </div>
          <div className="highlight-visual">
            <div className="phone-mockup">
              <img
                src={insightsScreenshot}
                alt="Trenda app showing the Insights screen with blood glucose, insulin profile, carbohydrate profile and exercise charts"
                className="phone-screenshot"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA BANNER ── */}
      <section className="cta-section">
        <div className="section-inner cta-inner">
          <h2>Ready to get Trenda?</h2>
          <p>Free to download. No subscription required.</p>
          <a
            href="https://apps.apple.com"
            className="btn-primary btn-large"
            target="_blank"
            rel="noopener noreferrer"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
            </svg>
            Download on the App Store
          </a>
        </div>
      </section>

    </main>
  );
}
